import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useMemo } from 'react';
import useTimeout from '../../hooks/use-timeout.esm.js';
import { get, omit, EMPTY_OBJECT } from '../../utils/object.esm.js';
import { getGrid, omitGrid } from '../../utils/props-utils.esm.js';
import Text from '../_primitives/text/text.esm.js';
import FormField from '../form-field/form-field.esm.js';
import useCallbackRef from '../../hooks/use-callback-ref.esm.js';

var _excluded = ["field", "form", "label", "labelProps", "required", "helperText", "formatValue", "labelInfoIcon", "options", "onBlur", "onChange", "onFocus"];
var DEBUG = process.env.NODE_ENV !== 'production' && false;
var bindFns = function bindFns() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }
  return function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    fns.forEach(function (fn) {
      return fn && fn.apply(void 0, args);
    });
  };
};
var Indicator = /*#__PURE__*/React.createElement(Text, {
  color: "danger"
}, "*");
function formikField(Component) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$disableBlurEvent = _ref.disableBlurEvent,
    disableBlurEvent = _ref$disableBlurEvent === void 0 ? false : _ref$disableBlurEvent,
    _ref$disableFocusEven = _ref.disableFocusEvent,
    disableFocusEvent = _ref$disableFocusEven === void 0 ? false : _ref$disableFocusEven,
    _ref$hasFieldWrapper = _ref.hasFieldWrapper,
    hasFieldWrapper = _ref$hasFieldWrapper === void 0 ? false : _ref$hasFieldWrapper,
    _ref$isCheckbox = _ref.isCheckbox,
    isCheckbox = _ref$isCheckbox === void 0 ? false : _ref$isCheckbox,
    _ref$isCheckboxGroup = _ref.isCheckboxGroup,
    isCheckboxGroup = _ref$isCheckboxGroup === void 0 ? false : _ref$isCheckboxGroup,
    _ref$isAutoComplete = _ref.isAutoComplete,
    isAutoComplete = _ref$isAutoComplete === void 0 ? false : _ref$isAutoComplete,
    _ref$isSelectMenu = _ref.isSelectMenu,
    isSelectMenu = _ref$isSelectMenu === void 0 ? false : _ref$isSelectMenu,
    _ref$useValue = _ref.useValue,
    useValue = _ref$useValue === void 0 ? false : _ref$useValue;
  return function FormikField(_ref2) {
    var _ref2$field = _ref2.field,
      field = _ref2$field === void 0 ? {} : _ref2$field,
      _ref2$form = _ref2.form,
      form = _ref2$form === void 0 ? {} : _ref2$form,
      label = _ref2.label,
      labelProps = _ref2.labelProps,
      required = _ref2.required,
      helperText = _ref2.helperText,
      formatValue = _ref2.formatValue,
      labelInfoIcon = _ref2.labelInfoIcon,
      options = _ref2.options,
      onBlurProp = _ref2.onBlur,
      onChangeProp = _ref2.onChange,
      onFocusProp = _ref2.onFocus,
      props = _objectWithoutProperties(_ref2, _excluded);
    var name = field.name,
      fieldOnBlur = field.onBlur,
      fieldOnChange = field.onChange,
      fieldOnFocus = field.onFocus;
    var _setFieldTouched = form.setFieldTouched,
      _setFieldValue = form.setFieldValue;
    // use ref to decouple formik non-memorable props
    var setFieldValue = useCallbackRef(_setFieldValue);
    var setFieldTouched = useCallbackRef(_setFieldTouched);
    var isTouched = get(form, "touched.".concat(name));
    var error = get(form, "errors.".concat(name));
    var value = get(form, "values.".concat(name));
    var validationText = isTouched && error ? error : undefined;
    if (props.validationText) {
      validationText = props.validationText;
    }
    var state = isTouched && error ? 'danger' : undefined;
    if (props.state) {
      state = props.state;
    }
    var _props$componentsProp = props.componentsProps,
      componentsProps = _props$componentsProp === void 0 ? EMPTY_OBJECT : _props$componentsProp;
    var overrideProps = isAutoComplete ? {
      componentsProps: _objectSpread2({
        input: {
          state: state
        }
      }, componentsProps)
    } : {
      state: state
    };
    if (hasFieldWrapper) {
      overrideProps = _objectSpread2(_objectSpread2({}, overrideProps), {}, {
        a11yId: name,
        a11yLabel: label,
        isRequire: required
      });
    }
    if (isCheckbox) {
      var checked = false;
      if (value) {
        checked = Boolean(value);
      }
      if (props.checked) {
        checked = props.checked;
      }
      overrideProps = _objectSpread2(_objectSpread2({}, overrideProps), {}, {
        checked: checked,
        isInvalid: !!validationText
      });
    }
    var delayExecute = useTimeout(0);
    var _useMemo = useMemo(function () {
        // eslint-disable-next-line no-console
        // console.log(` ---- formik useMemo [${name}] ----\r `)
        var onBlur = fieldOnBlur;
        var onChange = fieldOnChange;
        var onFocus = fieldOnFocus;
        if (isSelectMenu || isAutoComplete) {
          if (isAutoComplete) {
            onBlur = function onBlur() {
              // eslint-disable-next-line no-console
              // console.log(`--- formikField onBlur ---`)
              delayExecute(function () {
                return setFieldTouched(name);
              });
            };
            onChange = function onChange(_e, newValues) {
              setFieldValue(name, newValues);
            };
          } else {
            onBlur = function onBlur() {
              return setFieldTouched(name);
            };
            onChange = function onChange(_value, _option, newValues) {
              return setFieldValue(name, newValues);
            };
          }
        }
        if (isCheckboxGroup) {
          onChange = function onChange(value) {
            return setFieldValue(name, value);
          };
          onBlur = function onBlur() {
            return setFieldTouched(name);
          };
        }
        if (useValue) {
          onChange = function onChange(value) {
            return setFieldValue(name, value);
          };
          if (isAutoComplete) {
            delayExecute(function () {
              return setFieldTouched(name);
            });
          }
        }
        return {
          onBlur: bindFns(disableBlurEvent ? function () {} : onBlur, onBlurProp),
          onChange: bindFns(onChange, onChangeProp),
          onFocus: bindFns(disableFocusEvent ? function () {} : onFocus, onFocusProp)
        };
      }, [fieldOnBlur, fieldOnChange, fieldOnFocus, onBlurProp, onChangeProp, onFocusProp, delayExecute, setFieldTouched, name, setFieldValue]),
      onBlur = _useMemo.onBlur,
      onChange = _useMemo.onChange,
      onFocus = _useMemo.onFocus;
    overrideProps = _objectSpread2(_objectSpread2(_objectSpread2({}, overrideProps), formatValue ? {
      value: formatValue(value)
    } : useValue ? {
      value: value
    } : {}), {}, {
      onBlur: onBlur,
      onChange: onChange,
      onFocus: onFocus
    });
    if (DEBUG && name === 'compSetPermissions') {
      // eslint-disable-next-line no-console
      console.log(" ---- formik adapter [".concat(name, "] ----\r\n   form: ").concat(JSON.stringify(form, undefined, 2), "\r\n  -------- \r\n   field: ").concat(JSON.stringify(omit(field, ['name']), undefined, 2), "\r\n  -------- \r\n   overrideProps: ").concat(JSON.stringify(omit(overrideProps, ['onBlur', 'onChange', 'onFocus']), undefined, 2), " \r\n -------- "));
    }
    return hasFieldWrapper ? /*#__PURE__*/React.createElement(FormField, _extends({
      label: label,
      labelProps: labelProps,
      labelIndicator: required ? Indicator : null,
      validationText: validationText,
      helperText: helperText,
      labelInfoIcon: labelInfoIcon
    }, getGrid(props)), /*#__PURE__*/React.createElement(Component, _extends({}, isAutoComplete ? {
      options: options,
      isInvalid: !!validationText
    } : {}, omitGrid(props), field, overrideProps))) : /*#__PURE__*/React.createElement(Component, _extends({}, isAutoComplete ? {
      options: options
    } : {}, props, field, _objectSpread2({
      label: label,
      labelProps: labelProps,
      isRequired: required,
      validationText: validationText,
      state: validationText ? 'danger' : undefined
    }, overrideProps)));
  };
}

export { formikField };
//# sourceMappingURL=fields.esm.js.map
